let variantCartQuantity = 0;

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
        this.variantChangeTimeout = null;
        this.productVariants = this.getProductVariants();
      }

      getProductVariants() {
        const variantsScript = this.querySelector('variant-selects [data-product-variants]');
        if (variantsScript) {
          try {
            return JSON.parse(variantsScript.innerHTML);
          } catch (e) {
            console.error('Error parsing product variants:', e);
            return [];
          }
        }
        return [];
      }

      getVariantFromOptions(selectedOptionValues) {
        if (!this.productVariants || this.productVariants.length === 0) return null;
        
        return this.productVariants.find(variant => {
          return variant.options.every((option, index) => {
            const selectedOption = this.querySelector(`variant-selects`)
              ?.querySelectorAll('select option[selected], fieldset input:checked')[index];
            return selectedOption && option === selectedOption.value;
          });
        });
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      updatePriceImmediately(variant) {
        if (!variant) return;

        const priceElement = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceElement) return;

        const priceContainer = priceElement.querySelector('.price__container');
        if (!priceContainer) return;

        const regularPrice = priceContainer.querySelector('.price-item--regular');
        const salePrice = priceContainer.querySelector('.price-to-display');
        const savingsElement = priceContainer.querySelector('.price__savings');
        
        const formatMoneyNoCurrency = (cents) => {
          if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
            try {
              return Shopify.formatMoney(cents, '{{amount}}');
            } catch (e) {
              console.log('Error Raised ', e)
            }
          }

          const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
          return amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        };

        const displayPriceNoCurrency = formatMoneyNoCurrency(variant.price);
        const comparePriceNoCurrency = variant.compare_at_price ? formatMoneyNoCurrency(variant.compare_at_price) : '';

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          if (regularPrice) {
            regularPrice.textContent = this.formatMoney(variant.compare_at_price);
          }
          if (salePrice) {
            salePrice.textContent = displayPriceNoCurrency;
          }
          
          if (savingsElement) {
            const savingsPercent = Math.round(((variant.compare_at_price - variant.price) * 100.0) / variant.compare_at_price);
            savingsElement.textContent = `-${savingsPercent}%`;
            savingsElement.classList.remove('hidden');
          }
          
          priceContainer.classList.add('price--on-sale');
        } else {
          if (regularPrice) {
            regularPrice.textContent = this.formatMoney(variant.price);
          }
          
          if (savingsElement) {
            savingsElement.classList.add('hidden');
          }
          
          priceContainer.classList.remove('price--on-sale');
        }

        const priceDisplayNodes = priceContainer.querySelectorAll('#price-to-display, .price-to-display');
        priceDisplayNodes.forEach((node) => {
          node.textContent = displayPriceNoCurrency;
          node.dataset.price = displayPriceNoCurrency;
        });

        // Update discounted price display (sales banner) - it's outside priceContainer
        const discountedPriceDisplayNode = this.querySelector('#discounted-price-display');
        if (discountedPriceDisplayNode) {
          const discount = parseFloat(discountedPriceDisplayNode.dataset.discount) || 0;
          const discountInPaise = discount * 100;
          const discountedPrice = variant.price - discountInPaise;
          const discountedPriceFormatted = formatMoneyNoCurrency(discountedPrice);
          discountedPriceDisplayNode.textContent = discountedPriceFormatted;
          discountedPriceDisplayNode.dataset.price = discountedPriceFormatted;
        }

        const comparePriceNodes = priceContainer.querySelectorAll('#compare-price-to-display');
        comparePriceNodes.forEach((node) => {
          node.textContent = comparePriceNoCurrency;
          node.dataset.comparePrice = comparePriceNoCurrency;
        });

        this.updateCartQuantity(variant.id);

        const submitButton = this.querySelector(`#ProductSubmitButton-${this.dataset.section}`);
        if (submitButton) {
          if (variant.available) {
            submitButton.removeAttribute('disabled');
          } else {
            submitButton.setAttribute('disabled', 'disabled');
          }
        }

        priceElement.style.opacity = '1';
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
        }
        const amount = (cents / 100).toString();
        const formattedDollars = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return `${formattedDollars}`
      }

      updateCartQuantity(variantId) {
        const cartQuantityElement = this.querySelector('.quantity__rules-cart');
        const quantityCartSpan = this.querySelector('.quantity-cart');
        
        if (!cartQuantityElement || !quantityCartSpan) return;

        if (typeof fetch !== 'undefined') {
          fetch(window.Shopify?.routes?.root + 'cart.js')
            .then(response => response.json())
            .then(cart => {
              let variantQty = 0;
              if (cart.items) {
                cart.items.forEach(item => {
                  if (item.variant_id === variantId) {
                    variantQty += item.quantity;
                  }
                });
              }

              if (variantQty > 0) {
                quantityCartSpan.textContent = `(${variantQty} in cart)`;
                cartQuantityElement.classList.remove('hidden');
              } else {
                cartQuantityElement.classList.add('hidden');
              }
            })
            .catch(error => {
              console.error('Error fetching cart:', error);
            });
        }
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // Get the selected variant immediately
        const variant = this.getVariantFromOptions(selectedOptionValues);
        
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        // If same product, handle everything client-side (NO API CALL!)
        if (!shouldSwapProduct && variant) {
          // Clear any pending timeout
          if (this.variantChangeTimeout) {
            clearTimeout(this.variantChangeTimeout);
          }

          // Update everything client-side instantly
          this.updateVariantClientSide(variant, productUrl);
          return;
        }

        // Only make API call if swapping to different product
        this.resetProductFormState();

        if (this.variantChangeTimeout) {
          clearTimeout(this.variantChangeTimeout);
        }

        // Update price immediately before fetch
        if (variant) {
          this.updatePriceImmediately(variant);
        }

        this.variantChangeTimeout = setTimeout(() => {
          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
            targetId: target.id,
            callback: shouldSwapProduct
              ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
              : this.handleUpdateProductInfo(productUrl),
          });
        }, 50);
      }

      updateVariantClientSide(variant, productUrl) {
        // Update price
        this.updatePriceImmediately(variant);
        
        // Update URL
        this.updateURL(productUrl, variant?.id);
        
        // Update variant inputs
        this.updateVariantInputs(variant?.id);

        // Update quantity input
        this.quantityInput.value = this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1
        
        // Update SKU if exists
        const skuElement = this.querySelector(`#Sku-${this.dataset.section}`);
        if (skuElement && variant.sku) {
          skuElement.textContent = variant.sku;
          skuElement.classList.remove('hidden');
        } else if (skuElement) {
          skuElement.classList.add('hidden');
        }

        // Update inventory if exists
        const inventoryElement = this.querySelector(`#Inventory-${this.dataset.section}`);
        if (inventoryElement && variant.inventory_management) {
          const inventoryQty = variant.inventory_quantity || 0;
          if (inventoryQty > 0) {
            inventoryElement.textContent = `${inventoryQty} in stock`;
            inventoryElement.classList.remove('hidden');
          } else {
            inventoryElement.classList.add('hidden');
          }
        }

        // Update featured media if variant has one
        if (variant.featured_media?.id) {
          this.querySelector(`media-gallery`)?.setActiveMedia?.(
            `${this.dataset.section}-${variant.featured_media.id}`,
            true
          );
        }

        // Publish variant change event
        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: this.sectionId,
            variant,
          },
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error('Network response was not ok');
            return response.text();
          })
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // Use requestAnimationFrame for smoother focus transition
            requestAnimationFrame(() => {
              document.querySelector(`#${targetId}`)?.focus();
            });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          // Batch DOM updates for better performance
          requestAnimationFrame(() => {
            updateSourceFromDestination('price');
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
            updateSourceFromDestination('Volume');
            updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

            this.updateQuantityRules(this.sectionId, html);
            this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
            this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

            this.productForm?.toggleSubmitButton(
              html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
              window.variantStrings.soldOut
            );
          });

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        // Defer media gallery updates to not block price/button updates
        requestAnimationFrame(() => {
          const mediaGallerySource = this.querySelector('media-gallery ul');
          const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

          const refreshSourceData = () => {
            if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
            const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
            const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
            const sourceMap = new Map(
              mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
            );
            return [mediaGallerySourceItems, sourceSet, sourceMap];
          };

          if (mediaGallerySource && mediaGalleryDestination) {
            let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            const mediaGalleryDestinationItems = Array.from(
              mediaGalleryDestination.querySelectorAll('li[data-media-id]')
            );
            const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
            let shouldRefresh = false;

            // add items from new data not present in DOM
            for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
              if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
                mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
                shouldRefresh = true;
              }
            }

            // remove items from DOM not present in new data
            for (let i = 0; i < mediaGallerySourceItems.length; i++) {
              if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
                mediaGallerySourceItems[i].remove();
                shouldRefresh = true;
              }
            }

            if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

            // if media galleries don't match, sort to match new data order
            mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
              }
            });
          }

          // set featured media as active in the media gallery
          this.querySelector(`media-gallery`)?.setActiveMedia?.(
            `${this.dataset.section}-${variantFeaturedMediaId}`,
            true
          );

          // update media modal
          const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
          const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
          if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        });
      }

      setQuantityBoundries = () => {

        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;

        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        console.log('cartInput max value', max)
        console.log('cartInput min value', min)

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }

        this.quantityInput.value = min;

        const price_to_display = document.querySelectorAll('#price-to-display');
        const discountedPriceDisplay = document.querySelectorAll('#discounted-price-display');
        discountedPriceDisplay.forEach(element => {
          element.innerHTML = element.dataset.price;
        });
        price_to_display.forEach(element => {
          element.innerHTML = element.dataset.price;
        });

        const compare_price_to_display = document.querySelectorAll('#compare-price-to-display');
        compare_price_to_display.forEach(element => {
          element.innerHTML = element.dataset.comparePrice;
        });

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        
        if (!currentVariantId) return;
        
        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
        .then((response) => response.text())
        .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];

            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                // current.setAttribute(attribute, valueUpdated);
                // if (attribute === 'data-cart-quantity') {
                //   this.quantityInput.value = Number(valueUpdated) ? Number(valueUpdated) : 1;
                // }
                
                // Price Cart Update on clicking on add-to-cart button
                if (valueUpdated && attribute === 'data-cart-quantity') {
                  const price_cart_quantity = this.querySelectorAll('#price--quantity-cart');
                  price_cart_quantity.forEach(element => {
                    element.innerHTML = `(${valueUpdated} in cart)`
                  });
                }
                // console.log('[1] attribute', attribute, 'value', valueUpdated);
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
